import { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@stores/store';
import { Module } from '@stores/Modules/types/modules';
import { useLazyGetUserModuleGamesQuery } from '@api/userModules';
import { CustomDropdown, FullPageLoader, ShapeIcon } from '@components';
import { ShapeType } from '../../../components/ShapeIcon/ShapeIcon';
import ChartLayout from '../components/Underwritting/Charts';
import { ChartType, TOAST_TYPES } from '@constants/constants';
import UnderwritingDefinitionsSidePanel from '../components/Underwritting/DefinitionSidePanel';
import useCustomDashboardFilters from '@hooks/api/useCustomDashboardFilters';
import { useLazyGetUnderwritingChartsQuery } from '@api/charts';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { UnderwritingChart, UnderwritingChartsApiResponse } from '@api/types/underwriting';
import UnderwritingDownloadPdf from '../components/Underwritting/UnderwritingDownloadPdf';
import CustomButton from '../../../components/CustomButton/CustomButton';
import { setToastMessage } from '@stores/App/slice';
import { Game } from '../components/FundingEligibility/types';
import { setDashboardFilter } from '@stores/DashboardFilters/slice';

interface UnderwrittingChart {
  name: string;
  id: string;
  chartType: ChartType;
  metadata: string;
}

interface TabbedCharts {
  label: string;
  id: string;
  value: string;
  charts: UnderwrittingChart[];
}

const UnderwrittingDashboard: FC<{ id: string }> = ({ id: submoduleId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const dashboardFilters = useSelector((state: RootState) => state?.DashboardFiltersData);
  const modules = useSelector((state: RootState) => state?.ModulesData);

  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<string>('');
  const [tabbedCharts, setTabbedCharts] = useState<Record<string, TabbedCharts>>();
  const [showUnderwritingDefinitionsSidePanel, setShowUnderwritingDefinitionsSidePanel] =
    useState(false);
  const [selectedFilter, setSelectedFilter] = useState<{ dates: string[]; gameId: string }>({
    dates: [],
    gameId: '',
  });
  const [filterOptions, setFilterOptions] = useState<
    { gameId: string; dealName: string; dates: string[] }[]
  >([]);
  const [definitions, setDefinitions] = useState<{ label: string; description: string }[]>([]);

  const cohortAnalysisSelections = dashboardFilters.dashboards['CohortAnalysis' as any] as Game;

  // APIs
  const { getDashboardFilterValues, isLoading: isLoadingDashboardFilterValues } =
    useCustomDashboardFilters();
  const [getUnderwritingCharts, { isLoading: isLoadingUnderwritingCharts }] =
    useLazyGetUnderwritingChartsQuery();
  const [getUserModuleGames, { isLoading: isLoadingUserModuleGames }] =
    useLazyGetUserModuleGamesQuery();

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    if (modules.loadCount === 0) return;

    let underwrittingModule: Module | null = null;
    for (const module of modules.modules) {
      for (const submodule of module.submodules || []) {
        if (submodule.id === submoduleId) {
          underwrittingModule = module;
          break;
        }
      }
    }

    if (!underwrittingModule) return;
    const parsedMetadata = JSON.parse(underwrittingModule.metadata || '{}');

    if (!parsedMetadata.tabs) return;

    // Set default tab
    setTab(parsedMetadata.tabs[0].value);
  }, [modules]);

  useEffect(() => {
    if (!submoduleId) return;
    if (!selectedFilter.gameId) return;

    // Fetch charts based on submoduleId
    fetchCharts(submoduleId);
  }, [submoduleId, selectedFilter]);

  const constructTabbedChartData = (charts: UnderwritingChartsApiResponse) => {
    const tabs: Record<string, TabbedCharts> = {};

    for (const dataSection of charts.data) {
      const tab: TabbedCharts = {
        label: dataSection.name,
        id: dataSection.id,
        value: dataSection.id,
        charts: [...dataSection.charts].sort((a: UnderwritingChart, b: UnderwritingChart) => {
          // Handle null/undefined rowOrder values
          if (a.rowOrder == null && b.rowOrder == null) return 0;
          if (a.rowOrder == null) return 1;
          if (b.rowOrder == null) return -1;
          return a.rowOrder - b.rowOrder;
        }),
      };

      tabs[dataSection.id] = tab;
    }

    setTabbedCharts(tabs);

    const firstTab = Object.values(tabs)[0];
    if (firstTab) {
      setTab(firstTab.id);
    }
  };

  const fetchCharts = async (submoduleId: string) => {
    try {
      // const response = await getChartData(submoduleId);
      const response = await getUnderwritingCharts({
        submoduleId,
        queryParams: constructQueryParams(),
      }).unwrap();

      constructTabbedChartData(response);
    } catch (error) {
      console.error('Error fetching charts:', error);
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Error fetching charts, please try again later.',
        }),
      );
    }
  };

  const fetchFilters = async () => {
    if (!submoduleId) return;

    const { game_id: filterOptions } = await getDashboardFilterValues(submoduleId);
    const gameIdList: string[] = filterOptions?.values?.map((f: any) => f.game_id) || [];

    if (gameIdList.length > 0) {
      const deals = await getUserModuleGames({
        submoduleId: submoduleId,
        gamesParam: gameIdList,
      }).unwrap();

      const gameLevelFilters =
        filterOptions?.values
          ?.map((f: any) => ({
            gameId: f.game_id,
            dealName: deals.data.find((d: any) => d.gameId === f.game_id)?.dealName
              ? `${deals.data.find((d: any) => d.gameId === f.game_id)?.dealName} - ${f.game_id}`
              : `${f.game_id}`,
            dates: JSON.parse(f.cohorts),
          }))
          ?.sort((a: any, b: any) => a.dealName.localeCompare(b.dealName)) || [];

      setFilterOptions(gameLevelFilters);

      if (gameLevelFilters.length > 0) {
        if (cohortAnalysisSelections) {
          setSelectedFilter({
            ...selectedFilter,
            gameId: cohortAnalysisSelections.id,
            dates:
              gameLevelFilters.find((g: any) => g.gameId === cohortAnalysisSelections.id)?.dates ||
              [],
          });
        } else {
          const defaultFilterId = modules?.modules
            ?.flatMap((m: any) => m.submodules)
            ?.find((s: any) => s.id === submoduleId)?.defaultFilter;

          const defaultGame =
            gameLevelFilters?.find((g: any) => g.gameId === defaultFilterId) ||
            gameLevelFilters?.[0];
          setSelectedFilter({
            ...selectedFilter,
            gameId: defaultGame?.gameId,
            dates: defaultGame?.dates,
          });
        }
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  const constructQueryParams = (): string => {
    const qps: string[] = [];

    if (selectedFilter.gameId) {
      qps.push(`gameId=${selectedFilter.gameId}`);
    }

    if (selectedFilter.dates) {
      qps.push(`cohort=${selectedFilter.dates}`);
    }

    return qps.length > 0 ? `?${qps.join('&')}` : '';
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const getTabIcon = (index: number) => {
    const shapes: ShapeType[] = ['hexagon', 'square', 'triangle', 'pentagon', 'circle'];
    return shapes[index % shapes.length];
  };

  const renderChartsSection = useMemo(() => {
    return (tabbedCharts || {})[tab]?.charts.map((item) => (
      <ChartLayout
        filters={{
          ...selectedFilter,
          gameId: selectedFilter.gameId,
        }}
        chartTitle={item.name}
        showDefinitionSidePanel={() => {
          setShowUnderwritingDefinitionsSidePanel(true);
        }}
        setDefinitions={setDefinitions}
        chartType={item.chartType}
        submoduleId={submoduleId}
        chartId={item.id}
      />
    ));
  }, [tabbedCharts, tab, selectedFilter]);

  if (isLoadingDashboardFilterValues || isLoadingUnderwritingCharts || isLoadingUserModuleGames) {
    return <FullPageLoader />;
  }

  return (
    <Stack id={`pvx-underwriting-container`} p={'2rem'} height={'100vh'} overflow={'scroll'}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Stack direction={'row'} gap={'0.5rem'} alignItems={'center'}>
          <Typography fontSize={'26px'} lineHeight={'30px'} fontWeight={700} color={'#162936'}>
            Cohort Analysis {filterOptions.length > 0 && ':'}
            {filterOptions?.length === 1 ? filterOptions[0]?.dealName : ''}
          </Typography>
          {filterOptions?.length > 1 ? (
            <CustomDropdown
              dropdownOptions={filterOptions.map((option) => ({
                id: option.gameId,
                label: option.dealName,
              }))}
              handleChange={(e) => {
                dispatch(setDashboardFilter({ CohortAnalysis: { id: e.target.value } }));
                setSelectedFilter({
                  ...selectedFilter,
                  gameId: e.target.value,
                  dates:
                    filterOptions.find((option) => option.gameId === e.target.value)?.dates || [],
                });
              }}
              selectedOption={selectedFilter.gameId || ''}
              wrapperSx={{
                fontSize: isMobile ? '1rem' : '1.5rem',
                color: '#1B1C17',
                lineHeight: 'none',
                border: '1px solid #2F736E46',
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: '15rem',
                  },
                },
              }}
            />
          ) : (
            <></>
          )}
        </Stack>

        <Typography fontSize={'14px'} lineHeight={'20px'} fontWeight={700} color={'#6C6C6C'}>
          $=USD
        </Typography>
      </Stack>

      {/* Date Range Filter */}
      <Box pt={'1.5rem'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
        <Box display={'flex'} flexDirection={'row'} gap={'0.5rem'} alignItems={'center'}>
          <FormControl sx={{ minWidth: 200, maxWidth: 300 }}>
            <Select
              labelId="dates-multi-select-label"
              id="dates-multi-select"
              multiple
              value={selectedFilter.dates}
              onChange={(event) => {
                const selectedDates = event.target.value;
                if (selectedDates?.length === 0) {
                  return;
                }
                setSelectedFilter({
                  ...selectedFilter,
                  dates: [...selectedDates],
                });
              }}
              renderValue={(selected) => {
                if (selected.length === 0) return 'Select Cohorts';
                if (
                  selected.length ===
                  filterOptions.find((f) => f.gameId === selectedFilter.gameId)?.dates?.length
                )
                  return 'All Cohorts';

                const maxDisplayLength = 20; // Maximum characters to display
                const joinedText = selected.join(', ');

                if (joinedText.length <= maxDisplayLength) {
                  return joinedText;
                }

                // Show first few items with ellipsis
                let displayText = '';
                let itemCount = 0;

                for (const item of selected) {
                  const nextText = displayText ? `${displayText}, ${item}` : item;
                  if (nextText.length > maxDisplayLength - 3) {
                    // Reserve 3 chars for '...'
                    break;
                  }
                  displayText = nextText;
                  itemCount++;
                }

                const remainingCount = selected.length - itemCount;
                return remainingCount > 0 ? `${displayText}... (+${remainingCount})` : displayText;
              }}
              sx={{
                height: '3rem',
                width: '15rem',
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    width: 250,
                    marginTop: '0.5rem',
                  },
                },
              }}
            >
              {/* Extract unique dates from filterOptions */}
              {filterOptions
                .find((f) => f.gameId === selectedFilter.gameId)
                ?.dates.map((date) => (
                  <MenuItem key={date} value={date}>
                    <Checkbox checked={selectedFilter.dates.indexOf(date) > -1} />
                    <ListItemText primary={date} />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              color: '#51B8B0',
              cursor: 'pointer',
            }}
            onClick={() =>
              setSelectedFilter({
                ...selectedFilter,
                dates: filterOptions.find((f) => f.gameId === selectedFilter.gameId)?.dates || [],
              })
            }
          >
            Select All
          </Typography>
        </Box>

        {isOpen ? (
          <CustomButton
            name="Downloading..."
            onClick={() => {}}
            startIcon={PictureAsPdfOutlinedIcon}
            color="#6C6C6C"
          ></CustomButton>
        ) : (
          <CustomButton
            name="Download Report"
            onClick={() => setIsOpen(true)}
            startIcon={PictureAsPdfOutlinedIcon}
            color="#51B8B0"
          ></CustomButton>
        )}
      </Box>
      <Stack gap={'18px'} mt={'18px'}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="underwritting tabs"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            flexDirection: 'column',
            alignItems: 'flex-start',
            backgroundColor: 'white',
            borderRadius: '12px 12px 0px 0px',
            borderBottom: '1px solid #F0F0F1',
            '.MuiTabs-indicator': {
              backgroundColor: '#51B8B0',
              height: '2px',
              borderRadius: '5px',
              overflowX: 'auto',
            },
            '.MuiTabs-scroller': {
              overflowX: 'auto !important',
              scrollbarWidth: 'thin',
            },
          }}
        >
          {Object.values(tabbedCharts || {}).map((tab, index) => (
            <Tab
              key={`tab-${tab.value}`}
              icon={<ShapeIcon shape={getTabIcon(index)} size={12} />}
              iconPosition="start"
              label={tab.label}
              value={tab.id}
              sx={{
                paddingY: '0px',
                height: '48px',
                minHeight: '48px',
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: 700,
                textTransform: 'none',
                color: '#6C6C6C',
                '&.Mui-selected': {
                  color: '#51B8B0',
                },
                alignItems: 'center',
              }}
            />
          ))}
        </Tabs>
      </Stack>

      {/* Tab Panel Section */}
      <Box mt={'18px'} display={'flex'} flexDirection={'column'} gap={'1rem'}>
        {/* <UnderwrittingNotes /> */}

        {renderChartsSection}
      </Box>
      <UnderwritingDefinitionsSidePanel
        definitions={definitions}
        showUnderwritingDefinitionsSidePanel={showUnderwritingDefinitionsSidePanel}
        setShowUnderwritingDefinitionsSidePanel={setShowUnderwritingDefinitionsSidePanel}
      />
      <UnderwritingDownloadPdf
        isOpen={isOpen}
        dealName={filterOptions.find((f) => f.gameId === selectedFilter.gameId)?.dealName || ''}
        handleClose={handleClose}
        tabbedCharts={tabbedCharts}
        tab={tab}
        filters={{
          ...selectedFilter,
          gameId: selectedFilter.gameId,
        }}
        showDefinitionSidePanel={() => {
          setShowUnderwritingDefinitionsSidePanel(true);
        }}
        submoduleId={submoduleId}
      />
    </Stack>
  );
};

export default UnderwrittingDashboard;
